import i18n from '@/i18n'
import { categoryService } from '../../../services/category.service'
import { mapActions, mapGetters } from 'vuex'
import { SnackbarStyle, EventHub, REGEX } from '@/constants/constants'
import { DialogCategory } from "@/tools/dialog"
import TreeSelectorComponent from '@/components/commons/tree-selector/TreeSelectorComponent.vue'
import { clone } from 'ramda'
import AutocompleteComponent from "@/components/commons/autocomplete/AutocompleteComponent.vue"

export default {
    name: 'CategoryDialogComponent',
    components: {
        TreeSelectorComponent,
        AutocompleteComponent
    },
    props: {
        // v-model injects the object to be edited into props.value
        model: {
            type: Object,
            required: true
        }
    },
    data: () => ({
        // model del formulario
        validForm: false,
        // reglas de validación
        validationRules: {
            required: (value) => !!value || `${i18n.t('required')}`,
            logo: [(value) => !value || value.size < 2000000 || `${i18n.t('error.imageSize')}`,
            (value) => !value || REGEX.imageType.test(value.name) || `${i18n.t('error.imageType')}`],
        },
        // datos del tree selector
        dataSelector: [
            { id: 'zones', name: i18n.t('category.editDialog.zone'), isDisabled: false },
            { id: 'pois', name: i18n.t('category.editDialog.poi'), isDisabled: false }
        ],
        // errores para el campo del núm de línea
        nameError: [],
        // model para el dialog
        showEditDialog: false,
        // título del dialog
        editDialogTitle: i18n.t('category.editDialog.titleNew'),
        // normalizer para el treeselect
        normalizer: (node) => {
            return {
                id: node.id,
                label: node.name,
                children: node.children
            }
        },
        // empresas
        enterprises: [],
        // zona y/o poi seleccionados
        selectedZonePoiIds: [],
        // nombre actual
        actualName: null,
        // logo seleccionado
        logoSelected: null,
        // por validación del los nodos del selector
        zonesEditable: true,
        poisEditable: true,
        checkCount: 0
    }),
    computed: {
        /**
         * Breadcrumbs de los dialog
         */
        ...mapGetters('breadcrumbs', [
            'getBread'
        ]),
        /**
         * Src del logo
         */
        logoSource() {
            if (this.model.logo) {
                return 'data:image/png;base64,' + this.model.logo
            } else {
                return null;
            }
        }
    },
    created() {
        this.actualName = this.model.name

        /**
         * El modelo de datos del diálogo se carga a través del objeto del breadcrumb
         */
        this.unwatch = this.$store.watch(
            (state, getters) => getters['breadcrumbs/getBread'],
            (newBreads) => {
                const lastBreadcrumb = this.getBread[newBreads.length - 1]
                if (!lastBreadcrumb) {
                    this.cancel()
                } else {
                    if (lastBreadcrumb.key === DialogCategory) {
                        if (lastBreadcrumb.model) {
                            const modelData = this.model
                            Object.assign(modelData, lastBreadcrumb.model)
                        }
                    }
                }
            },
            { deep: true })
    },
    mounted() {
        /**
         * El modelo de datos del diálogo se carga a través del objeto del breadcrumb
         */
        const lastBreadcrumb = this.getBread[this.getBread.length - 1]
        if (!lastBreadcrumb) {
            this.cancel()
        } else {
            if (lastBreadcrumb.key === DialogCategory) {
                if (lastBreadcrumb.model) {
                    const modelData = this.model
                    Object.assign(modelData, lastBreadcrumb.model)
                }
            }
        }
    },
    beforeDestroy() {
        // elimino la instancia del watcher del breadcrumb
        this.unwatch()
    },
    methods: {
        /**
         * actions del snackbar
         */
        ...mapActions('snackbar', [
            'showSnackbar',
            'closeSnackbar'
        ]),
        /**
         * actions del dialog
         */
        ...mapActions('dialog', [
            'openDialog',
            'closeDialog'
        ]),
        /**
         * actions del breadcrumb
         */
        ...mapActions('breadcrumbs', [
            'pullBread',
            'pushBread'
        ]),
        /**
         * Mensaje de error cuando el nombre está duplicado
         */
        nameDuplicatedMethod() {
            if (this.model.name === this.actualName) return
            categoryService.validateCategoryName(this.model.name).then(response => {
                if (response) {
                    this.nameError.push(`${i18n.t('category.duplicate')}`)
                } else {
                    this.nameError = []
                }
            })
        },
        /**
         * Resetea los campos del formulario de creación/actualización
         */
        resetFields() {
            if (this.$refs.form) {
                this.$refs.form.reset()
            }
            this.checkCount = 0
        },
        /**
         * Click fuera del dialog
         */
        clickOutside() {
            this.cancel()
        },
        /**
         * Cancelar
         */
        cancel() {
            this.pullBread(DialogCategory)
            this.resetFields()
        },
        /**
         * Guardar
         */
        save() {
            const categoryDto = this.getCategoryDto(this.model.update)
            if (this.model.update) {
                categoryService.update(categoryDto, this.selectedZonePoiIds).then(() => {
                    this.showSnackbar({
                        visible: true,
                        text: this.$t('category.successUpdateMessage'),
                        timeout: 6000,
                        style: SnackbarStyle.SUCCESS
                    })
                    this.resetFields()
                    this.$emit('commitCategory')
                    this.cancel()
                    this.$eventBus.$emit(EventHub.COMMIT_CATEGORY, categoryDto)
                })
            } else {
                categoryService.create(categoryDto, this.selectedZonePoiIds).then(insertedId => {
                    this.showSnackbar({
                        visible: true,
                        text: this.$t('category.successCreateMessage'),
                        timeout: 6000,
                        style: SnackbarStyle.SUCCESS
                    })
                    this.resetFields()
                    this.$emit('commitCategory')
                    this.cancel()
                    categoryDto._id = insertedId
                    this.$eventBus.$emit(EventHub.COMMIT_CATEGORY, categoryDto)
                })
            }
        },
        /**
         * Genera el DTO para actualizar / crear un grupo
         * @param {*} update
         */
        //TODO VER CAMPO de applications si va acá o se manda como parámetro en create y update
        getCategoryDto(update = false) {
            const result = {
                name: this.model.name,
                enterpriseId: this.model.enterpriseIdNotBinded,
                color: this.model.color,
                logo: this.model.logo,
                zones: false,
                pois: false
            }
            if(this.selectedZonePoiIds > 0){
                this.selectedZonePoiIds.forEach(id => {
                    switch(id){
                        case 'zones':
                            result.zones = true;
                            break;
                        case 'pois':
                            result.pois = true;
                    }
                })
            }
            if (update) {
                result._id = this.model._id
            }
            return result
        },
        /**
         * Se ejecuta con el evento input del vue-treeselect para cambiar el valor del arreglo selectedZonePoiIds
         * @param {*} data
         */
        setSelectedZonePoiIds(data) {
            this.selectedZonePoiIds = data
        },
        /**
         * Cada vez que se modifica la selección de la empresa, se cargan
         */
        enterpriseSelectedFunction(newValue) {
            this.model.enterpriseIdNotBinded = newValue._id
        },
        /**
         * Cuando selecciona la imagen, convierte a BASE64 y la asigna al model
         * @param {*} files
         */
        onFileSelected(file) {
            if (file) {
                // base 64 (para guardar en el modelo)
                const binaryReader = new FileReader()
                binaryReader.readAsBinaryString(file)
                binaryReader.onload = () => {
                this.model.logo = btoa(binaryReader.result)
                }
                binaryReader.onerror = () => {
                    console.error('Error al leer la imagen subida');
                }
            } else {
                this.model.logo = null
            }
        },
        /**
         * Elimina el logo
         */
        deleteLogo() {
            this.model.logo = null
            this.logoSelected = null
            this.logoErrors = []
        }
    },
    watch: {
        model: {
            deep: true,
            immediate: true,
            handler(newModel) {
                this.enterprises = clone(newModel.enterprises)
                if (this.checkCount === 0) {
                    if (newModel.zones) {
                        this.selectedZonePoiIds.push(this.dataSelector[0].id)
                    }
                    if (newModel.pois) {
                        this.selectedZonePoiIds.push(this.dataSelector[1].id)
                    }
                    !newModel.zonesEditable && this.model.update ?
                        this.dataSelector[0].isDisabled = true
                        : this.dataSelector[0].isDisabled = false
                    
                    !newModel.poisEditable && this.model.update ?
                        this.dataSelector[1].isDisabled = true
                        : this.dataSelector[1].isDisabled = false
                }
                this.checkCount += 1
            }
        }
    }
    
}